import { calendarSchema, ZInferType } from '@/schemas'
import { BaseLibType, PatientType, PaymentType, RangesType, UserType, WorkHour } from '@/types'

type CalendarApi = {
  prev: () => void
  next: () => void
  today: () => void
  gotoDate: (date: Date | number) => void
  changeView: (viewName: string) => void
}

export type CalendarRef = {
  getApi: () => CalendarApi
}

export type CalendarDirection = 'prev' | 'next' | 'today'
export type CalendarViewType = 'day' | 'month' | 'year'

export type ChangeDatePayload = {
  value: string
  type: CalendarViewType
}

export enum CalendarViewEnum {
  MONTH = 'dayGridMonth',
  WEEK = 'timeGridWeek',
  DAY = 'timeGridDay',
}

export type CalendarRemindersType = {
  channels: string[]
  unit: string
  amount: number
}

export type CalendarSettingsType = {
  mode: string | null
  consultation_duration?: number | null
  buffer_before?: number | null
  buffer_after?: number | null
  consultation_fee?: number | null
  doctors?: UserType[]
  priority_doctor_id?: string | null
  work_hours: WorkHour[] | null
  ranges: RangesType[] | null
  reminder_settings?: CalendarRemindersType[] | null
}

export type CalendarInputSettings = Omit<CalendarSettingsType, 'doctors'> & {
  doctors?: string[]
}

export type ViewedDates = {
  start: Date
  end: Date
  startStr: string
  endStr: string
  timeZone: string
}

export type LocationType = BaseLibType & {
  work_hours: WorkHour[]
  ranges: RangesType[]
}

export type DoctorAvailabilityType = {
  free_slots: string[]
  conflicts: boolean
  events: {
    start: string
    end: string
  }[]
}

export type CalendarEventType = {
  id: string
  title: string
  description?: string
  message?: string
  start_at: string
  end_at: string
  location: LocationType
  patient: PatientType
  participants: UserType[]
  doctors?: UserType[]
  bill_type: EventBillingTypeEnum
  visit_type: EventVisitTypeEnum
  status: EventStatusEnum
  payment?: PaymentType
  procedures?: string[]
  reminders?: CalendarEventRemindersType[]
}

export type CalendarEventRemindersType = {
  id: string
  scheduled_at: string
  sent_at: string | null
  status: EventReminderStatusEnum
  attempts: number
  failure_reason: string | null
  reminder_settings: CalendarRemindersType[]
}

export enum EventReminderStatusEnum {
  PENDING = 'pending',
  SENT = 'sent',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

export type CalendarEventWithInfoType = {
  clinic: LocationType
  events: CalendarEventType[]
  doctors: UserType[]
}

export type CalendarEventFormType = ZInferType<typeof calendarSchema>

export enum EventBillingTypeEnum {
  INSURANCE = 'insurance',
  UNSURE = 'unsure',
  NON_BILLABLE = 'non_billable',
  ELECTIVE = 'elective',
}

export enum EventVisitTypeEnum {
  VIRTUAL_CONSULTATION = 'virtual_consultation',
  VIRTUAL_FOLLOW_UP = 'virtual_follow_up',
  PHONE_CONSULTATION = 'phone_consultation',
  CONSULTATION = 'consultation',
  PRE_TREATMENT = 'pre_treatment',
  PROCEDURE_TREATMENT = 'procedure_treatment',
  FOLLOW_UP = 'follow_up',
}

export enum EventStatusEnum {
  SCHEDULED = 'scheduled',
  CANCELLED = 'cancelled',
  NO_SHOW = 'no_show',
  COMPLETED = 'completed',
}

export type SlotItem = { key: string; hm: string }
export type SlotsByDate = Record<string, string[]>
export type SlotsByDoctor = Record<string | number, SlotsByDate>

export type FreeSlotType = {
  start: string
  end: string
  start_tz: string
  end_tz: string
}

export type DoctorType = {
  id: string | number
  free_slots: FreeSlotType[]
  consultation_duration?: number
  buffer_before?: number
  buffer_after?: number
  priority_doctor?: boolean
}

export type PublicCalendarEventType = {
  id: string
  title: string
  description?: string | null
  start_at: string
  end_at: string
  location: LocationType
  patient: PatientType | null
  doctors?: DoctorType[]
  bill_type: EventBillingTypeEnum
  visit_type: EventVisitTypeEnum
  status: EventStatusEnum
  payment?: PaymentType
  has_payment?: boolean
  can_cancel?: boolean
  can_reschedule?: boolean
  use_timezone?: boolean
  message?: string | null
}
